import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout } from '@/utils/routerHelper'
import { NO_RESET_WHITE_LIST } from '@/constants'

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/views/NavHome/NavHome.vue'),
    name: 'NavHome',
    meta: {
      hidden: true,
      title: '导航首页',
      noTagsView: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'RedirectWrap',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/auth',
    redirect: '/auth/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Auth',
    meta: {
      hidden: true,
      title: '认证',
      noTagsView: true
    },
    children: [
      {
        path: 'login',
        component: () => import('@/views/Login/Login.vue'),
        name: 'Login',
        meta: {
          hidden: true,
          title: '登录',
          noTagsView: true
        }
      },
      {
        path: 'register',
        component: () => import('@/views/Login/Login.vue'),
        name: 'Register',
        meta: {
          hidden: true,
          title: '注册',
          noTagsView: true
        }
      }
    ]
  },
  {
    path: '/confirm-email',
    component: () => import('@/views/ConfirmEmail/ConfirmEmail.vue'),
    name: 'ConfirmEmail',
    meta: {
      hidden: true,
      title: '确认邮箱',
      noTagsView: true
    }
  },
  {
    path: '/password-change',
    component: () => import('@/views/ResetPassword/ResetPassword.vue'),
    name: 'PasswordChange',
    meta: {
      hidden: true,
      title: '重置密码',
      noTagsView: true
    }
  },
  {
    path: '/personal',
    component: Layout,
    redirect: '/personal/personal-center',
    name: 'Personal',
    meta: {
      title: '个人中心',
      hidden: true,
      canTo: true
    },
    children: [
      {
        path: 'personal-center',
        component: () => import('@/views/Personal/PersonalCenter/PersonalCenter.vue'),
        name: 'PersonalCenter',
        meta: {
          title: '个人中心',
          hidden: true,
          canTo: true
        }
      }
    ]
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/customer-management',
    component: Layout,
    name: 'CustomerManagement',
    meta: {
      title: '客户管理',
      icon: 'customer',
      order: 1
    },
    children: [
      {
        path: '',
        component: () => import('@/views/CustomerManagement/CustomerManagement.vue'),
        name: 'CustomerManagementIndex',
        meta: {
          title: '客户管理',
          noCache: true
        }
      }
    ]
  },
  {
    path: '/operations-user-management',
    component: Layout,
    name: 'OperationUserManagement',
    meta: {
      title: '运维用户管理',
      icon: 'operations',
      order: 2
    },
    children: [
      {
        path: '',
        component: () => import('@/views/OperationUserManagement/OperationUserManagement.vue'),
        name: 'OperationUserManagementIndex',
        meta: {
          title: '运维用户管理',
          noCache: true
        }
      }
    ]
  },
  {
    path: '/agent-management',
    component: Layout,
    name: 'AgentManagement',
    meta: {
      title: '智能体管理',
      icon: 'agent',
      order: 3
    },
    children: [
      {
        path: '',
        component: () => import('@/views/AgentManagement/AgentManagement.vue'),
        name: 'AgentManagementIndex',
        meta: {
          title: '智能体管理',
          noCache: true
        }
      }
    ]
  },
  {
    path: '/agent-template',
    component: Layout,
    name: 'AgentTemplate',
    meta: {
      title: '智能体模板',
      icon: 'template',
      order: 5,
      fullHeight: true
    },
    children: [
      {
        path: '',
        component: () => import('@/views/AgentTemplate/AgentTemplate.vue'),
        name: 'AgentTemplateIndex',
        meta: {
          title: '智能体模板',
          noCache: true,
          fullHeight: true
        }
      }
    ]
  },
  {
    path: '/menu-management',
    component: Layout,
    name: 'MenuManagement',
    meta: {
      title: '菜单管理',
      icon: 'menuManagement',
      order: 6,
      permission: ['superadmin']
    },
    children: [
      {
        path: '',
        component: () => import('@/views/MenuManagement/MenuManagement.vue'),
        name: 'MenuManagementIndex',
        meta: {
          title: '菜单管理',
          noCache: true,
          permission: ['superadmin']
        }
      }
    ]
  },
  {
    path: '/role-management',
    component: Layout,
    name: 'RoleManagement',
    meta: {
      title: '角色管理',
      icon: 'roleManagement',
      order: 7,
      permission: ['superadmin', 'admin']
    },
    children: [
      {
        path: '',
        component: () => import('@/views/RoleManagement/RoleManagement.vue'),
        name: 'RoleManagementIndex',
        meta: {
          title: '角色管理',
          noCache: true,
          permission: ['superadmin', 'admin']
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
